export const VEHICLE_STATUSES = ['available', 'coming_soon', 'sold_out', 'inactive'] as const;
export const VEHICLE_CLASSES = ['X', 'S', 'A', 'B', 'C', 'D'] as const;
export const RESERVATION_STATUSES = ['PENDING', 'SIGNED', 'CONFIRMED', 'READY', 'COMPLETED', 'CANCELLED', 'EXPIRED'] as const;
export const ROLES = ['ADMIN', 'MANAGER', 'SALESPERSON'] as const;
export const SALESPERSON_TIERS = ['TIER_1', 'TIER_2', 'TIER_3'] as const;
export const COMMISSION_TIERS = [...SALESPERSON_TIERS, 'MANAGER'] as const;

export type VehicleStatus = (typeof VEHICLE_STATUSES)[number];
export type VehicleClass = (typeof VEHICLE_CLASSES)[number];
export type ReservationStatus = (typeof RESERVATION_STATUSES)[number];
export type Role = (typeof ROLES)[number];
export type SalespersonTier = (typeof SALESPERSON_TIERS)[number];
export type CommissionTier = (typeof COMMISSION_TIERS)[number];

export interface VehicleImage { id: number; vehicle_id: number; url: string; mime_type: string; alt_text: string; sort_order: number; is_primary: number }

export interface Vehicle {
  id: number;
  slug: string;
  brand: string;
  model: string;
  display_name: string;
  internal_code: string;
  description: string;
  category: string;
  category_slug: string;
  price: number;
  original_price: number | null;
  sale_price: number | null;
  discount_percentage: number;
  on_sale: number;
  sale_start: string | null;
  sale_end: string | null;
  likes_count: number;
  year: number;
  engine: string;
  horsepower: number;
  top_speed: number;
  acceleration: number;
  weight: number;
  traction: 'FWD' | 'RWD' | 'AWD';
  transmission: 'Manual' | 'Automática' | 'Secuencial';
  fuel_type: 'Gasolina' | 'Diésel' | 'Eléctrico' | 'Híbrido';
  doors: number | null;
  seats: number | null;
  doors_known: number;
  seats_known: number;
  technical_specs_known: number;
  performance_specs_known: number;
  provider_image_hidden: number;
  stock_total: number;
  stock_reserved: number;
  stock_available: number;
  class_a_rotation: number;
  status: VehicleStatus;
  vehicle_class: VehicleClass | null;
  catalogue_visible: number;
  featured: number;
  handling_score: number;
  acceleration_score: number;
  braking_score: number;
  speed_score: number;
  created_at: string;
  updated_at: string;
  primary_image: string | null;
  source_vehicle_type?: string | null;
  source_gears?: number | null;
  tax_percent?: number;
  price_without_tax?: number;
  images?: VehicleImage[];
}

export interface Reservation {
  id: number;
  code: string;
  vehicle_id: number;
  customer_id: number;
  character_name: string;
  state_id: string;
  phone: string;
  comments: string | null;
  status: ReservationStatus;
  signed_by_employee_id?: number | null;
  signed_by_name?: string | null;
  confirmed_at?: string | null;
  automatic_ready_at?: string | null;
  reservation_expires_at: string | null;
  base_price_at_confirmation: number | null;
  total_price_at_confirmation: number | null;
  deposit_amount: number | null;
  remaining_amount?: number | null;
  created_at: string;
  updated_at: string;
  vehicle_name?: string;
  vehicle_price_without_tax?: number;
  vehicle_slug?: string;
  primary_image?: string | null;
}

export interface ApiSuccess<T> { success: true; data: T; meta?: { page: number; limit: number; total: number; pages: number } }
export interface ApiFailure { success: false; error: { code: string; message: string; details?: unknown } }
export type ApiResponse<T> = ApiSuccess<T> | ApiFailure;

export interface EmployeeSession { id: number; username: string; displayName: string; role: Role; commissionTier: SalespersonTier | null; onDuty: boolean; csrfToken: string }

export interface PublicStats { availableVehicles: number; brands: number; activeReservations: number; soldVehicles: number }

export interface PricingSettings {
  upchargePercent: number;
  classUpcharges: Partial<Record<VehicleClass, number>>;
  taxPercent: number;
  wholesaleTaxPercent: number;
  saleTaxPercent: number;
  reservationsEnabled: boolean;
  updatedAt: string;
}

export interface CommissionRate { tier: CommissionTier; label: string; ratePercent: number }

export interface EmployeeSale {
  id: number;
  employee_id: number;
  vehicle_name: string;
  customer_name: string;
  employee_name: string;
  sale_price: number;
  commission_tier: CommissionTier | null;
  commission_rate_percent: number;
  commission_amount: number;
  commission_finalized: number;
  commission_paid_at: string | null;
  commission_paid_by_employee_id: number | null;
  created_at: string;
}

export interface DashboardStats {
  totalVehicles: number;
  unitsInStock: number;
  soldOutVehicles: number;
  activeReservations: number;
  pendingReservations: number;
  completedSales: number;
  grossSales: number;
  wholesaleCost: number;
  purchaseTaxes: number;
  saleTaxes: number;
  totalCommissions: number;
  netProfit: number;
  activeOffers: number;
  totalLikes: number;
  topSold: Array<{ name: string; count: number }>;
  topReserved: Array<{ name: string; count: number }>;
  topLiked: Array<{ id: number; name: string; slug: string; count: number }>;
  salesSeries: Array<{ day: string; value: number }>;
  reservationSeries: Array<{ day: string; value: number }>;
  salesByCategory: Array<{ category: string; value: number }>;
}
